import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Comment as CommentIcon,
  Person as PersonIcon,
  ThumbUp as ThumbUpIcon,
  Repeat as RepeatIcon,
  Launch as LaunchIcon,
  LinkedIn as LinkedInIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Reply as ReplyIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { formatDistanceToNow, format } from 'date-fns';

// Import PostCard component
import PostCard from '../components/PostCard';

const CommentAssistant = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postsLoading, setPostsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetchingComments, setFetchingComments] = useState(false);
  const [postComments, setPostComments] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentingPost, setCommentingPost] = useState(null);
  const [commentLoading, setCommentLoading] = useState(false);
  const [openReplyDialog, setOpenReplyDialog] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyLoading, setReplyLoading] = useState(false);

  // Format date helper function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.abs(now - date) / 36e5; // Convert to hours

    if (diffInHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    } else if (diffInHours < 48) {
      return 'yesterday';
    } else {
      return format(date, 'MMM d, yyyy h:mm a');
    }
  };

  // Helper function to extract username from LinkedIn URL
  const extractUsername = (url) => {
    if (!url) return '';
    if (!url.includes('linkedin.com')) return url;
    
    const matches = url.match(/linkedin\.com\/in\/([^\/\?]+)/);
    return matches ? matches[1] : url;
  };

  // Update determinePostFormat function to better handle carousels and documents
  const determinePostFormat = (post) => {
    // First check for carousel/document
    if (post.document || 
        post.media?.type === 'document' || 
        (post.media?.items && post.media.items.length > 0) ||
        post.carousel_document_link) {
      return 'Carousel';
    }
    
    // Then check for video
    if (post.media?.type === 'video' || 
        post.media?.url?.includes('video') ||
        post.media?.url?.includes('.mp4')) {
      return 'Video';
    }
    
    // Then check for images
    if (post.media?.type === 'image' || 
        post.media?.url?.includes('/image/') || 
        post.media?.url?.includes('.jpg') || 
        post.media?.url?.includes('.jpeg') || 
        post.media?.url?.includes('.png') || 
        post.media?.url?.includes('.gif')) {
      return 'Image';
    }

    // Default to Text if no media
    return 'Text';
  };

  // Update getBestMediaUrl function to handle carousels better
  const getBestMediaUrl = (post) => {
    const format = determinePostFormat(post);
    
    switch(format) {
      case 'Carousel':
        // For carousels, prioritize document thumbnail
        return post.document?.thumbnail || 
               post.media?.thumbnail || 
               (post.media?.items && post.media.items[0]?.thumbnail) ||
               post.carousel_document_link || 
               '';
      
      case 'Video':
        // For videos, prioritize thumbnail
        return post.media?.thumbnail || 
               post.thumbnail || 
               post.media?.url || 
               '';
      
      case 'Image':
        // For images, prioritize full image URL
        return post.media?.url || 
               post.text_url || 
               post.media?.thumbnail || 
               post.thumbnail || 
               '';
      
      default:
        return '';
    }
  };

  // Fetch user profile and LinkedIn posts
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setPostsLoading(true);
        setError('');
        
        const profileRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        const userData = profileRes.data.user;
        
        if (userData?.linkedinProfile) {
          const username = extractUsername(userData.linkedinProfile);
          
          if (username) {
            console.log('Fetching posts for username:', username);
            const postsRes = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/linkedin/profile/posts`,
              {
                params: { username },
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
            
            let postsData = [];
            if (postsRes.data) {
              if (Array.isArray(postsRes.data)) {
                postsData = postsRes.data;
              } else if (postsRes.data.posts && Array.isArray(postsRes.data.posts)) {
                postsData = postsRes.data.posts;
              } else if (typeof postsRes.data === 'object') {
                const arrayProperty = Object.values(postsRes.data).find(val => Array.isArray(val));
                if (arrayProperty) {
                  postsData = arrayProperty;
                }
              }
            }
            
            // Format posts for display with proper media handling
            const formattedPosts = postsData.map(post => {
              // Log raw post data to debug media
              console.log('Raw post data:', {
                id: post.id,
                document: post.document,
                media: post.media,
                carousel_document_link: post.carousel_document_link,
                text_url: post.text_url,
                thumbnail: post.thumbnail
              });

              // Determine post format first
              const format = determinePostFormat(post);

              // Get the best media URL based on format
              const mediaUrl = getBestMediaUrl(post);

              console.log('Post format and media:', {
                id: post.id,
                format,
                mediaUrl
              });

              return {
                id: post.id || post.urn || `post-${Date.now()}-${Math.random()}`,
                content: post.content || post.text || '',
                author: {
                  name: post.author?.name || 
                        (post.author?.first_name && post.author?.last_name ? 
                          `${post.author.first_name} ${post.author.last_name}`.trim() : 
                          post.data?.author?.name || 
                          post.author?.headline?.split(' at ')[0] || 
                          'Unknown'),
                  profilePicture: post.author?.profilePicture || 
                                 post.author?.profile_picture || 
                                 post.data?.author?.profilePicture || 
                                 post.author?.image || 
                                 post.author?.imageUrl || 
                                 post.author?.pictureUrl || ''
                },
                postedAt: post.postedAt?.date || post.posted_at || new Date().toISOString(),
                stats: {
                  comments: post.stats?.comments || 0,
                  likes: post.stats?.like || post.stats?.likes || 0,
                  totalReactions: post.stats?.total_reactions || post.stats?.totalReactions || 0,
                  reposts: post.stats?.reposts || 0,
                  support: post.stats?.support || 0,
                  love: post.stats?.love || 0,
                  insight: post.stats?.insight || 0,
                  celebrate: post.stats?.celebrate || 0
                },
                format,
                url: post.url || '',
                media: {
                  url: mediaUrl,
                  thumbnail: post.media?.thumbnail || post.thumbnail || '',
                  type: format.toLowerCase(),
                  items: post.media?.items || []
                },
                document: post.document || null,
                carousel_document_link: post.carousel_document_link || null
              };
            });
            
            console.log('Formatted posts:', formattedPosts);
            setPosts(formattedPosts);
          }
        }
      } catch (err) {
        console.error('Error fetching LinkedIn posts:', err);
        setError('Failed to load LinkedIn posts. Please try again later.');
      } finally {
        setPostsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Fetch comments for LinkedIn posts
  const fetchPostComments = async (postId) => {
    try {
      setFetchingComments(true);
      
      // Extract the short ID from the post ID if it's a full URN
      const shortPostId = postId.includes(':') 
        ? postId.split(':').pop() 
        : postId;
      
      console.log('Fetching comments for post ID:', shortPostId);
      
      // Call our backend proxy endpoint
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/linkedin/post-comments`,
        {
          params: { postId: shortPostId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          timeout: 30000 // Increase timeout to 30 seconds
        }
      );
      
      // Handle the new response format
      let commentsData = [];
      
      if (response.data?.success === 1 && Array.isArray(response.data.results)) {
        console.log('Comments received:', response.data.results.length);
        commentsData = response.data.results;
      } else {
        console.warn('Unexpected response format:', response.data);
        commentsData = [];
      }
      
      // Format and save comments
      const comments = commentsData.map(comment => ({
        id: comment.id || uuidv4(),
        text: comment.comment || '',
        author: {
          name: comment.profileFullName || 'LinkedIn User',
          headline: comment.occupation || '',
          profileUrl: comment.profileUrl || '',
          profilePicture: ''
        },
        likeCount: 0,
        createdAt: comment.created_at || Date.now()
      })).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)); // Sort by date ascending
      
      // Update post comments
      setPostComments(prev => ({
        ...prev,
        [postId]: comments
      }));
      
      return comments;
    } catch (error) {
      console.error('Error fetching comments:', error);
      
      // Show a more specific error message to the user
      const errorMsg = error.response?.status === 500
        ? 'Server error when fetching comments. Please try again later.'
        : 'Failed to fetch comments. Please check your network connection.';
      
      enqueueSnackbar(errorMsg, { variant: 'error' });
      
      // Initialize with empty array to prevent repeated failed requests
      setPostComments(prev => ({
        ...prev,
        [postId]: []
      }));
      
      return [];
    } finally {
      setFetchingComments(false);
    }
  };

  // Handle selecting a post to comment on
  const handleSelectPost = async (post) => {
    setSelectedPost(post);
    
    // Only fetch comments if the post has comments and they haven't been loaded yet
    if (post.stats.comments > 0 && !postComments[post.id]) {
      try {
        await fetchPostComments(post.id);
      } catch (err) {
        console.error('Error fetching comments for selected post:', err);
        enqueueSnackbar('Failed to load comments for this post', { variant: 'warning' });
      }
    } else if (post.stats.comments === 0 && !postComments[post.id]) {
      // If post has no comments, set empty array to prevent future fetches
      setPostComments(prev => ({
        ...prev,
        [post.id]: []
      }));
    }
  };

  // Function to handle opening the comment dialog
  const handleOpenCommentDialog = (post) => {
    setCommentingPost(post);
    setCommentText('');
    setOpenCommentDialog(true);
  };

  // Function to handle closing the comment dialog
  const handleCloseCommentDialog = () => {
    setOpenCommentDialog(false);
    setCommentingPost(null);
    setCommentText('');
  };

  // Function to post a comment to LinkedIn
  const handlePostComment = async () => {
    if (!commentingPost || !commentText.trim()) {
      enqueueSnackbar('Please enter a comment', { variant: 'warning' });
      return;
    }

    // Check comment length against LinkedIn's maximum
    if (commentText.length > 700) {
      enqueueSnackbar('Comment is too long. LinkedIn allows maximum 700 characters', { variant: 'warning' });
      return;
    }

    setCommentLoading(true);
    try {
      // Extract the activity URN from the post - use the correct URN from the post object
      const activityUrn = commentingPost.urn || commentingPost.id;
      
      console.log('Posting comment to LinkedIn post:', {
        postId: activityUrn,
        commentLength: commentText.length
      });
      
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      // Use the correct API base URL with fallback
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.instation.app';
      const backendUrl = window.location.origin.includes('localhost') 
        ? 'http://localhost:5000' 
        : apiUrl;
      
      // Make API call to LinkedIn to post the comment
      const response = await axios.post(
        `${backendUrl}/api/linkedin/post-comment`, 
        {
          activityUrn,
          comment: commentText
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        enqueueSnackbar('Comment posted successfully to LinkedIn!', { variant: 'success' });
        
        // Update local comment count
        if (commentingPost) {
          const updatedPosts = posts.map(post => {
            if (post.urn === commentingPost.urn || post.id === commentingPost.id) {
              return {
                ...post,
                stats: {
                  ...post.stats,
                  comments: (post.stats?.comments || 0) + 1
                }
              };
            }
            return post;
          });
          setPosts(updatedPosts);
          
          // Update postComments state to include the new comment
          const postId = commentingPost.urn || commentingPost.id;
          setPostComments(prev => ({
            ...prev,
            [postId]: [
              ...(prev[postId] || []),
              {
                id: Date.now().toString(),
                text: commentText,
                author: {
                  name: user?.name || 'You',
                  headline: user?.headline || '',
                },
                createdAt: new Date().toISOString()
              }
            ]
          }));
        }
        
        handleCloseCommentDialog();
      } else {
        throw new Error(response.data.message || 'Failed to post comment');
      }
    } catch (error) {
      console.error('Error posting comment:', error);
      enqueueSnackbar(
        error.response?.data?.message || error.message || 'Failed to post comment. Please try again.', 
        { variant: 'error' }
      );
    } finally {
      setCommentLoading(false);
    }
  };

  // Handle opening the reply dialog
  const handleOpenReplyDialog = (comment) => {
    setReplyingTo(comment);
    setReplyText('');
    setOpenReplyDialog(true);
  };

  // Handle closing the reply dialog
  const handleCloseReplyDialog = () => {
    setOpenReplyDialog(false);
    setReplyingTo(null);
    setReplyText('');
  };

  // Function to post a reply to a comment
  const handlePostReply = async () => {
    if (!replyingTo || !replyText.trim() || !selectedPost) {
      enqueueSnackbar('Please enter a reply', { variant: 'warning' });
      return;
    }

    // Check reply length against LinkedIn's maximum
    if (replyText.length > 700) {
      enqueueSnackbar('Reply is too long. LinkedIn allows maximum 700 characters', { variant: 'warning' });
      return;
    }

    setReplyLoading(true);
    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      // Use the correct API base URL with fallback
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.instation.app';
      const backendUrl = window.location.origin.includes('localhost') 
        ? 'http://localhost:5000' 
        : apiUrl;

      // Format the post URN
      const postUrn = selectedPost.id.includes('ugcPost:') 
        ? selectedPost.id 
        : `urn:li:ugcPost:${selectedPost.id}`;

      // Format the comment URN - extract just the comment ID
      const commentId = replyingTo.id.includes('comment:') 
        ? replyingTo.id.split('comment:')[1].replace(/[()]/g, '') 
        : replyingTo.id;

      const response = await axios.post(
        `${backendUrl}/api/linkedin/post-reply`,
        {
          postUrn,
          parentCommentId: commentId,
          message: {
            text: replyText
          }
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        enqueueSnackbar('Reply posted successfully to LinkedIn!', { variant: 'success' });
        
        // Update local comments state to include the new reply
        setPostComments(prev => ({
          ...prev,
          [selectedPost.id]: [
            ...(prev[selectedPost.id] || []),
            {
              id: response.data.replyUrn || `reply-${Date.now()}-${Math.random()}`,
              text: replyText,
              author: {
                name: user?.name || 'You',
                headline: user?.headline || '',
              },
              createdAt: new Date().toISOString(),
              parentCommentId: replyingTo.id
            }
          ]
        }));
        
        handleCloseReplyDialog();
      } else {
        throw new Error(response.data.message || 'Failed to post reply');
      }
    } catch (error) {
      console.error('Error posting reply:', error);
      enqueueSnackbar(
        error.response?.data?.message || error.message || 'Failed to post reply. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setReplyLoading(false);
    }
  };

  const renderCommentDialog = () => {
    if (!commentingPost) return null;
    
    const isCommentTooLong = commentText.length > 700;

  return (
      <Dialog
        open={openCommentDialog}
        onClose={handleCloseCommentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Add Comment
          </Typography>
          <IconButton onClick={handleCloseCommentDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2, mt: 1 }}>
          <TextField
            autoFocus
            multiline
            rows={6}
            variant="outlined"
            fullWidth
            placeholder="Write a comment..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            error={isCommentTooLong}
            helperText={isCommentTooLong ? "Comment exceeds LinkedIn's maximum character limit" : ""}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                fontSize: '0.95rem'
              }
            }}
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Typography 
              variant="caption" 
              color={isCommentTooLong ? "error" : "textSecondary"} 
              sx={{ ml: 'auto', fontWeight: isCommentTooLong ? 600 : 400 }}
            >
              {commentText.length}/700 characters
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleCloseCommentDialog}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              borderRadius: 1
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={commentLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            onClick={handlePostComment}
            disabled={!commentText.trim() || commentLoading || isCommentTooLong}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              bgcolor: '#0a66c2',
              borderRadius: 1,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#004182',
                boxShadow: 'none'
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(10, 102, 194, 0.4)',
                color: 'white'
              }
            }}
          >
            {commentLoading ? 'Posting...' : 'Post Comment'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Add renderReplyDialog function
  const renderReplyDialog = () => {
    if (!replyingTo) return null;
    
    const isReplyTooLong = replyText.length > 700;

    return (
      <Dialog
        open={openReplyDialog}
        onClose={handleCloseReplyDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Reply to {replyingTo.author.name}'s comment
          </Typography>
          <IconButton onClick={handleCloseReplyDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2, mt: 1 }}>
          <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {replyingTo.text}
            </Typography>
          </Box>
          
          <TextField
            autoFocus
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            placeholder="Write a reply..."
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            error={isReplyTooLong}
            helperText={isReplyTooLong ? "Reply exceeds LinkedIn's maximum character limit" : ""}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                fontSize: '0.95rem'
              }
            }}
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Typography 
              variant="caption" 
              color={isReplyTooLong ? "error" : "textSecondary"} 
              sx={{ ml: 'auto', fontWeight: isReplyTooLong ? 600 : 400 }}
            >
              {replyText.length}/700 characters
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleCloseReplyDialog}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              borderRadius: 1
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={replyLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            onClick={handlePostReply}
            disabled={!replyText.trim() || replyLoading || isReplyTooLong}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              bgcolor: '#0a66c2',
              borderRadius: 1,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#004182',
                boxShadow: 'none'
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(10, 102, 194, 0.4)',
                color: 'white'
              }
            }}
          >
            {replyLoading ? 'Posting...' : 'Post Reply'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Add renderPostCard function
  const renderPostCard = (post) => (
    <Card
              sx={{ 
        mb: 2,
                borderRadius: 2, 
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
        },
        bgcolor: selectedPost?.id === post.id ? 'rgba(72, 144, 254, 0.04)' : 'white'
      }}
      onClick={() => handleSelectPost(post)}
    >
      <CardHeader
        avatar={
          <Avatar 
            src={post.author.profilePicture}
            sx={{ width: 48, height: 48 }}
          >
            {post.author.name ? post.author.name[0] : <PersonIcon />}
          </Avatar>
        }
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2D3748' }}>
              {post.author.name}
            </Typography>
            <Chip
              label={post.format}
              size="small"
              sx={{
                bgcolor: theme => {
                  switch(post.format) {
                    case 'Carousel': return 'rgba(56, 161, 105, 0.1)';
                    case 'Image': return 'rgba(72, 144, 254, 0.1)';
                    case 'Video': return 'rgba(237, 137, 54, 0.1)';
                    default: return 'rgba(160, 174, 192, 0.1)';
                  }
                },
                color: theme => {
                  switch(post.format) {
                    case 'Carousel': return '#38A169';
                    case 'Image': return '#4890fe';
                    case 'Video': return '#ED8936';
                    default: return '#718096';
                  }
                },
                height: 20,
                '& .MuiChip-label': {
                  px: 1,
                  py: 0,
                  fontSize: '0.625rem',
                  fontWeight: 600
                }
              }}
            />
                </Box>
        }
        subheader={formatDate(post.postedAt)}
        action={
          <Tooltip title="View on LinkedIn">
            <IconButton 
              href={post.url} 
              target="_blank"
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{ 
                color: '#0A66C2',
                '&:hover': {
                  bgcolor: 'rgba(10, 102, 194, 0.08)'
                }
              }}
            >
              <LaunchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#4A5568',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mb: 2
          }}
        >
          {post.content}
              </Typography>
              
        {getBestMediaUrl(post) && (
          <Box 
            sx={{ 
              mb: 2,
              position: 'relative',
              borderRadius: 1,
              overflow: 'hidden',
              height: 200,
              bgcolor: 'rgba(0, 0, 0, 0.04)'
            }}
          >
            <Box
              component="img"
              src={getBestMediaUrl(post)}
              alt="Post media"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                console.error('Image load error:', e);
                e.target.style.display = 'none';
              }}
            />
          </Box>
        )}
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={<ThumbUpIcon sx={{ fontSize: '16px !important' }} />}
            label={post.stats.totalReactions}
            size="small"
            variant="outlined"
            sx={{
              borderColor: 'rgba(72, 144, 254, 0.5)',
              color: '#4890fe',
              '& .MuiChip-icon': {
                color: '#4890fe'
              }
            }}
          />
          <Chip
            icon={<CommentIcon sx={{ fontSize: '16px !important' }} />}
            label={post.stats.comments}
            size="small"
            variant="outlined"
            sx={{
              borderColor: 'rgba(56, 161, 105, 0.5)',
              color: '#38A169',
              '& .MuiChip-icon': {
                color: '#38A169'
              }
            }}
          />
          <Chip
            icon={<RepeatIcon sx={{ fontSize: '16px !important' }} />}
            label={post.stats.reposts}
            size="small"
            variant="outlined"
            sx={{
              borderColor: 'rgba(237, 137, 54, 0.5)',
              color: '#ED8936',
              '& .MuiChip-icon': {
                color: '#ED8936'
              }
            }}
          />
        </Box>
      </CardActions>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Grid container spacing={3}>
        {/* Header */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 2, 
            borderRadius: 2,
            bgcolor: '#ffffff',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                src={user?.image}
                sx={{
                  width: 48, 
                  height: 48,
                  mr: 2,
                  border: '2px solid #0A66C2'
                }}
              >
                {user?.name ? user.name.charAt(0) : <PersonIcon />}
              </Avatar>
              
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2D3748' }}>
                  {user?.name || 'Your Profile'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LinkedInIcon 
                    sx={{ 
                      color: '#0A66C2', 
                      fontSize: 16, 
                      mr: 0.5 
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {user?.linkedinProfile || 'LinkedIn Profile'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          </Grid>
        )}

        {/* Posts List - Left Column */}
        <Grid item xs={12} md={5} lg={4}>
                  <Paper
            elevation={0} 
                    sx={{
              height: 'calc(100vh - 200px)',
              overflow: 'auto',
                      borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              p: 2
            }}
          >
            {postsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress size={40} sx={{ color: '#4890fe' }} />
              </Box>
            ) : posts.length > 0 ? (
              <Box>
                {posts.map((post) => renderPostCard(post))}
              </Box>
            ) : (
              <Box sx={{ 
                p: 3, 
                textAlign: 'center', 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  No LinkedIn posts found
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Connect your LinkedIn profile to view posts
                    </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

        {/* Comments Section - Right Column */}
        <Grid item xs={12} md={7} lg={8}>
            <Paper 
              elevation={0} 
              sx={{ 
              height: 'calc(100vh - 200px)',
              overflow: 'auto',
                borderRadius: 2, 
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            {selectedPost ? (
              <Box>
                {/* Comments List */}
                <Box sx={{ p: 3 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 2 
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2D3748' }}>
                      Comments ({selectedPost.stats.comments})
                </Typography>
                    <Button
                      variant="contained"
                      startIcon={<CommentIcon />}
                      onClick={() => handleOpenCommentDialog(selectedPost)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        bgcolor: '#0a66c2',
                        '&:hover': {
                          bgcolor: '#004182'
                        }
                      }}
                    >
                      Add Comment
                    </Button>
              </Box>
              
                  {fetchingComments ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} sx={{ color: '#4890fe' }} />
                    </Box>
                  ) : postComments[selectedPost.id]?.length > 0 ? (
                    <List>
                      {postComments[selectedPost.id].map((comment, index) => (
                        <React.Fragment key={`${comment.id}-${index}`}>
                          <ListItem sx={{ px: 0, py: 2 }}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'primary.main' }}>
                                {comment.author.name[0]}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ mb: 0.5 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D3748' }}>
                                    {comment.author.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(comment.createdAt)}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography 
                                    variant="body2" 
                  sx={{ 
                                      mb: 0.5,
                                      color: '#4A5568'
                  }}
                >
                                    {comment.text}
                  </Typography>
                                  {comment.author.headline && (
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                      sx={{ 
                                        display: 'block',
                                        fontStyle: 'italic'
                      }}
                    >
                                      {comment.author.headline}
                        </Typography>
                                  )}
                                  <Button
                                    size="small"
                                    startIcon={<ReplyIcon />}
                                    onClick={() => handleOpenReplyDialog(comment)}
                                    sx={{ 
                                      mt: 1,
                                      textTransform: 'none',
                                      color: '#0a66c2',
                                      '&:hover': {
                                        bgcolor: 'rgba(10, 102, 194, 0.04)'
                                      }
                                    }}
                                  >
                                    Reply
                                  </Button>
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < postComments[selectedPost.id].length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No comments yet
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 1
              }}>
                <CommentIcon sx={{ fontSize: 40, color: '#A0AEC0', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  Select a post to view comments
                </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      {renderCommentDialog()}
      {renderReplyDialog()}
    </Container>
  );
};

export default CommentAssistant; 